import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './context/AuthContext';

const getMarcaColor = (marca) => {
    const marcaNormalizada = marca.toLowerCase();
    if (marcaNormalizada.includes('chevrolet')) return 'primary';
    if (marcaNormalizada.includes('gmc')) return 'dark';
    if (marcaNormalizada.includes('toyota')) return 'danger';
    if (marcaNormalizada.includes('jlr')) return 'success';
    if (marcaNormalizada.includes('hino')) return 'secondary';
    if (marcaNormalizada.includes('byd')) return 'info';
    if (marcaNormalizada.includes('gwm')) return 'warning';
    return 'light';
};

function GestionAgencias() {
    const { token } = useAuth();
    
    const [agencias, setAgencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mensaje, setMensaje] = useState('');
    
    const [formData, setFormData] = useState({ nombre: '', descripcion: '', marca: '', es_matriz: false });
    const [editingId, setEditingId] = useState(null);

    const fetchAgencias = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            // --- MODIFICADO ---
            const response = await fetch('/api/agencias', { headers });
            if (!response.ok) { throw new Error('Error al cargar las agencias.'); }
            const data = await response.json();
            setAgencias(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { if (token) { fetchAgencias(); } }, [token, fetchAgencias]);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMensaje('');
        const url = editingId ? `/api/agencias/${editingId}` : '/api/agencias';
        const method = editingId ? 'PUT' : 'POST';

        try {
            // --- MODIFICADO ---
            const response = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            
            setMensaje(editingId ? '¡Agencia actualizada!' : '¡Agencia creada!');
            resetForm();
            fetchAgencias();
        } catch (err) {
            setError(err.message);
        }
    };
    
    const handleDesactivarAgencia = async (agenciaId) => {
        if (window.confirm('¿Seguro que quieres desactivar esta agencia?')) {
            try {
                // --- MODIFICADO ---
                const response = await fetch(`/api/agencias/${agenciaId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                setMensaje('Agencia desactivada.');
                fetchAgencias();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const startEditing = (agencia) => {
        setEditingId(agencia.id);
        setFormData({
            nombre: agencia.nombre,
            descripcion: agencia.descripcion || '',
            marca: agencia.marca,
            es_matriz: agencia.es_matriz
        });
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ nombre: '', descripcion: '', marca: '', es_matriz: false });
    };

    if (loading) return <div className="text-center mt-5"><div className="spinner-border"></div></div>;

    return (
        <div className="container mt-4">
            <div className="row">
                <div className="col-md-4">
                    <h4 className="mb-3">{editingId ? 'Editando Agencia' : 'Crear Nueva Agencia'}</h4>
                    <div className="card shadow-sm">
                        <div className="card-body">
                            {error && <div className="alert alert-danger p-2">{error}</div>}
                            {mensaje && <div className="alert alert-success p-2">{mensaje}</div>}
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Nombre</label>
                                    <input type="text" name="nombre" value={formData.nombre} onChange={handleFormChange} className="form-control" required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Descripción</label>
                                    <input type="text" name="descripcion" value={formData.descripcion} onChange={handleFormChange} className="form-control" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Marca</label>
                                    <input type="text" name="marca" value={formData.marca} onChange={handleFormChange} className="form-control" required />
                                </div>
                                <div className="form-check mb-3">
                                    <input type="checkbox" name="es_matriz" checked={formData.es_matriz} onChange={handleFormChange} className="form-check-input" id="es_matriz" />
                                    <label className="form-check-label" htmlFor="es_matriz">Es Matriz</label>
                                </div>
                                <div className="d-grid gap-2">
                                    <button type="submit" className="btn btn-primary">{editingId ? 'Guardar Cambios' : 'Crear Agencia'}</button>
                                    {editingId && <button type="button" onClick={resetForm} className="btn btn-secondary">Cancelar Edición</button>}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-md-8">
                    <h4 className="mb-3">Lista de Agencias</h4>
                    <div className="list-group">
                        {agencias.map(agencia => (
                            <div key={agencia.id} className="list-group-item">
                                <div className="d-flex w-100 justify-content-between align-items-center">
                                    <div>
                                        <h6>
                                            <span className={`badge bg-${getMarcaColor(agencia.marca)} me-2`}>{agencia.marca}</span>
                                            <span className={`badge rounded-pill mb-2 ${agencia.activo ? 'bg-success' : 'bg-secondary'}`}>{agencia.activo ? 'Activa' : 'Inactiva'}</span>
                                            {agencia.nombre}
                                        </h6>
                                        <p className="mb-1 text-muted">
                                            {agencia.descripcion}
                                            <span className="ms-1 fst-italic">{agencia.es_matriz ? ' - Matriz' : ' - Sucursal'}</span>
                                        </p>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button onClick={() => startEditing(agencia)} className="btn btn-outline-primary btn-sm">Editar</button>
                                        {agencia.activo && (<button onClick={() => handleDesactivarAgencia(agencia.id)} className="btn btn-outline-danger btn-sm">Desactivar</button>)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GestionAgencias;